import is from '@sindresorhus/is';

import type { AiTransformationMatrixGrouping } from '../../../data-contract/ai-transformation-strategy';
import type { PositionArchitecture } from '../../../data-contract/position-management';
import { selectArchitecture } from '../../../position-management';
import type { UrlTileSubject } from '../../../url-tiles';

export const tilePriorityKindOf = (subject: UrlTileSubject): AiTransformationMatrixGrouping => {
    switch (subject.kind) {
        case 'position':
            return 'POSITION';
        case 'task':
            return 'TASK';
        case 'job-role':
            return 'ROLE';
        case 'job-level':
            return 'LEVEL';
    }
};

export const tilePriorityIdOf = (subject: UrlTileSubject): string => {
    switch (subject.kind) {
        case 'position':
            return subject.positionId;
        case 'task':
            return subject.taskId;
        case 'job-role':
        case 'job-level':
            return subject.profileId;
    }
};

/**
 * The Position Architecture whose strategy a tile's subject is prioritized in: a position's own; for a role or level,
 * one connected to its Job Architecture; for a task, which carries no architecture, the dashboard's or else the default
 * of those connected to a Job Architecture. The dashboard's architecture wins wherever it fits, so the tile and the
 * page beside it show the same priority.
 */
export const tilePriorityArchitectureIdOf = (
    subject: UrlTileSubject,
    architectures: readonly PositionArchitecture[],
    dashboardArchitectureId: string | undefined,
): string | undefined => {
    if (subject.kind === 'position') return subject.architectureId;
    const candidates = architectures.filter(({ connected_job_architecture_id: jobArchitectureId }) =>
        subject.kind === 'task' ? is.string(jobArchitectureId) : jobArchitectureId === subject.jobArchitectureId,
    );
    if (subject.kind === 'task' && dashboardArchitectureId !== undefined) return dashboardArchitectureId;
    if (candidates.some(({ id }) => id === dashboardArchitectureId)) return dashboardArchitectureId;

    return selectArchitecture(candidates, null)?.id;
};
